import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import * as actions from "../../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../../component/Toasts/Toasts";
import "../../../SuccessionPlanning/readable-form.scss";

const ScoreTab = (props: any) => {
  const { resourceAllocationRef, resourceName, managerScope } = props;
  const dispatch = useDispatch();

  // In the dedicated Manager view we hit the manager-scoped endpoints, which the
  // server restricts to the caller's own reportees. HR/full-app usage keeps the
  // default (unrestricted) endpoints untouched.
  const setupAction = managerScope
    ? actions.getScoringSetupForManagerAction
    : actions.getScoringSetupAction;
  const saveAction = managerScope
    ? actions.saveScoreCardForManagerAction
    : actions.saveScoreCardAction;
  const qualGetAction = managerScope
    ? actions.getQualitativeFeedbackForManagerAction
    : actions.getQualitativeFeedbackAction;
  const qualSaveAction = managerScope
    ? actions.saveQualitativeFeedbackForManagerAction
    : actions.saveQualitativeFeedbackAction;

  const [setup, setSetup] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [btnDisable, setBtnDisable] = useState(false);
  const [qualParams, setQualParams] = useState<any[]>([]);
  const [qualTexts, setQualTexts] = useState<any>({});
  const [qualMin, setQualMin] = useState(60);
  const [isSuccessor, setIsSuccessor] = useState(false);

  const userLogin: any = localStorage.getItem("login");
  const loginId = JSON.parse(userLogin || "{}")?.id;

  const loadSetup = () => {
    setLoading(true);
    dispatch(
      setupAction(resourceAllocationRef, (res: any) => {
        if (res?.data) {
          setSetup(res.data);
          setItems((res.data.items || []).map((it: any) => ({ ...it })));
        }
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    if (resourceAllocationRef) {
      loadSetup();
      loadQualitative();
    }
  }, [resourceAllocationRef]);

  const countWords = (text: any) =>
    String(text || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;

  const loadQualitative = () => {
    dispatch(
      qualGetAction(resourceAllocationRef, (res: any) => {
        if (res?.error) return;
        const data = res?.data?.data ?? res?.data;
        if (!data) return;
        setQualParams(data.params || []);
        setQualMin(data.minWords || 60);
        setIsSuccessor(!!data.isSuccessor);
        const next: any = {};
        (data.params || []).forEach((p: any) => {
          next[p.param] = data.latest?.[p.param]?.text || "";
        });
        setQualTexts(next);
      })
    );
  };

  const updateQualText = (param: string, value: any) => {
    setQualTexts((prev: any) => ({ ...prev, [param]: value }));
  };

  const updateScore = (idx: number, value: any) => {
    const next = [...items];
    next[idx] = { ...next[idx], score: value };
    setItems(next);
  };

  // Mirrors the SDK's "was a score provided" check, so a blank KRA is never
  // mistaken for a real score of 0 (which would count toward the total).
  const isScored = (v: any) => v !== null && v !== undefined && String(v).trim() !== "";

  const weightedOf = (row: any) => {
    if (!isScored(row.score)) return null;
    const w = Number(row.weight) || 0;
    const s = Number(row.score);
    return Math.round((w / 100) * s * 100) / 100;
  };
  const total =
    Math.round(items.reduce((sum, r) => sum + (weightedOf(r) ?? 0), 0) * 100) / 100;

  const handleSave = () => {
    // Flexible capture: every FILLED box must reach the minimum (server
    // re-validates); empty boxes stay Pending for later. Completeness is
    // gated downstream (report download / close-out), not here.
    for (const p of qualParams) {
      const words = countWords(qualTexts[p.param]);
      if (words > 0 && words < qualMin) {
        showErrorToastMessage(
          `"${p.label}" needs at least ${qualMin} words (${words} given) — complete it or clear it before saving.`
        );
        return;
      }
    }
    setBtnDisable(true);
    const payload = {
      resourceAllocationRef,
      createdBy: loginId,
      items: items.map((r) => ({
        kraRef: r.kraRef,
        kraTitle: r.kraTitle,
        weight: Number(r.weight) || 0,
        score: isScored(r.score) ? Number(r.score) : null,
      })),
    };
    dispatch(
      saveAction(payload, (res: any) => {
        if (res?.error) {
          setBtnDisable(false);
          const msg =
            res?.error?.response?.data?.error?.error_description ||
            "Failed to save scorecard";
          showErrorToastMessage(msg);
          return;
        }
        // Score saved — now the qualitative boxes (append-only history).
        const qualItems = qualParams
          .map((p) => ({ param: p.param, text: String(qualTexts[p.param] || "").trim() }))
          .filter((it) => it.text !== "");
        if (!qualItems.length) {
          setBtnDisable(false);
          showSuccessToastMessage("Scorecard saved successfully");
          loadSetup();
          return;
        }
        dispatch(
          qualSaveAction({ resourceAllocationRef, items: qualItems }, (qres: any) => {
            setBtnDisable(false);
            if (qres?.error) {
              const msg =
                qres?.error?.response?.data?.error?.error_description ||
                "Scorecard saved, but qualitative feedback failed to save";
              showErrorToastMessage(msg);
            } else {
              showSuccessToastMessage("Scorecard saved successfully");
            }
            loadSetup();
            loadQualitative();
          })
        );
      })
    );
  };

  // Export the current scorecard (KRA, weight, score, weighted score + total) to xlsx.
  const handleExport = () => {
    const header = ["KRA", "Weight (%)", "Score", "Weighted Score"];
    const rows = items.map((r) => [
      r.kraTitle,
      Number(r.weight) || 0,
      isScored(r.score) ? Number(r.score) : "",
      weightedOf(r) ?? "",
    ]);
    const person = (resourceName || "").trim();
    const aoa = [
      [`Resource: ${person || "-"}`],
      [`Designation: ${setup.designationTitle}`],
      [],
      header,
      ...rows,
      ["Total Weighted Score", "", "", total],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scorecard");
    // Per person -> name the file after them (fall back to designation)
    const safe = String(person || setup.designationTitle || "scorecard").replace(/[^\w-]+/g, "_");
    XLSX.writeFile(wb, `Scorecard - ${safe}.xlsx`);
  };

  if (loading) return <p className="mt-3">Loading…</p>;

  if (!setup?.hasDesignation) {
    return <p className="employeedata mt-3">{setup?.message || "No designation mapped."}</p>;
  }
  if (!setup?.hasWeightage) {
    return <p className="employeedata mt-3">{setup?.message || "No weighted KRAs set for this designation."}</p>;
  }

  return (
    <div className="mt-3 readable-table">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ margin: 0 }}>
          Designation: <b>{setup.designationTitle}</b> — score each KRA below.
        </p>
        <button type="button" className="btn cancel-btn buttonsize" onClick={handleExport}>
          Export
        </button>
      </div>

      <div className="table table-responsive mt-2">
        <table className="table-main table table-sm">
          <thead className="table-head">
            <tr className="table-head-row">
              <th className="table-head-row" scope="col" style={{ width: "45%" }}>KRA</th>
              <th className="table-head-row" scope="col" style={{ width: "15%" }}>Weight (%)</th>
              <th className="table-head-row" scope="col" style={{ width: "20%" }}>Score</th>
              <th className="table-head-row" scope="col" style={{ width: "20%" }}>Weighted Score</th>
            </tr>
          </thead>
          <tbody className="table-body">
            {items.map((row, idx) => (
              <tr className="table-body-row" key={row.kraRef || idx}>
                <td>{row.kraTitle}</td>
                <td>{row.weight}</td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    min={0}
                    max={100}
                    value={row.score}
                    onChange={(e) => updateScore(idx, e.target.value)}
                    style={{ maxWidth: 110, margin: "0 auto" }}
                  />
                </td>
                <td>{weightedOf(row) ?? "—"}</td>
              </tr>
            ))}
            <tr className="table-body-row">
              <td colSpan={3} style={{ textAlign: "right", fontWeight: "bold" }}>
                Total Weighted Score
              </td>
              <td style={{ fontWeight: "bold" }}>{total}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <p style={{ margin: 0, fontWeight: "bold" }}>
          Qualitative Feedback (
          {qualParams.filter((p) => countWords(qualTexts[p.param]) >= qualMin).length}/
          {qualParams.length || 5} complete)
          {isSuccessor && (
            <span style={{ color: "#b54708" }}>
              {" "}— {qualMin} words minimum per box
            </span>
          )}
        </p>
        {qualParams.map((p) => {
          const words = countWords(qualTexts[p.param]);
          const complete = words >= qualMin;
          const short = words > 0 && words < qualMin;
          return (
            <div className="mt-2" key={p.param}>
              <label style={{ fontWeight: 600 }}>
                {p.label} {isSuccessor && <span style={{ color: "red" }}>*</span>}
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={qualTexts[p.param] || ""}
                onChange={(e) => updateQualText(p.param, e.target.value)}
                placeholder={`Describe — at least ${qualMin} words${isSuccessor ? " (mandatory)" : ""}`}
              />
              <small style={{ color: complete ? "#1a7f37" : short ? "red" : "#667085" }}>
                {complete ? `✓ ${words}/${qualMin} words` : `${words}/${qualMin} words`}
              </small>
            </div>
          );
        })}
      </div>

      <div className="mt-3" style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          className="btn add-submit-button buttonsize"
          disabled={btnDisable}
          onClick={handleSave}
        >
          Save Scorecard
        </button>
      </div>
    </div>
  );
};

export default ScoreTab;
